const CAP6 = {
  id: "fundamentos-blockchain-6",
  slug: "caracteristicas-principales-erc20",
  title: "Características Principales de los Tokens ERC-20",
  sections: [
    {
      id: "hero",
      heading: "Interoperabilidad y Estandarización: el núcleo del ERC-20",
      text: `La creación del estándar ERC-20 respondió a una necesidad fundamental dentro del ecosistema Ethereum: 
garantizar la **interoperabilidad** entre los distintos tokens y contratos inteligentes.  
Antes de su aparición, cada token se comportaba de forma distinta, lo que generaba confusión y falta de compatibilidad entre plataformas.  
El ERC-20 unificó el lenguaje técnico que permite a todas las aplicaciones —carteras, intercambios, juegos y sistemas financieros— 
comunicarse entre sí utilizando un mismo conjunto de reglas.  
Esta estandarización convirtió a Ethereum en el epicentro de la innovación en blockchain y en el hogar de miles de activos digitales.`
    },
    {
      id: "estructura",
      heading: "Estructura y Propiedades Fundamentales",
      text: `Cada token ERC-20 sigue una estructura definida que determina su comportamiento, sus límites económicos 
y las reglas de interacción entre usuarios y contratos.  
Estas características son las que permiten que el token sea reconocido, transferido y auditado dentro del ecosistema.`,
      list: [
        {
          title: "Identificador, nombre y símbolo",
          text: `Cada token posee un **nombre (name)** y un **símbolo (symbol)** que actúan como su identidad pública.  
Por ejemplo, el token de Tether se llama “Tether USD” y su símbolo es “USDT”.  
Estos atributos son esenciales para distinguir miles de tokens diferentes en la blockchain, 
facilitando la visualización en billeteras, mercados y exploradores como Etherscan.`
        },
        {
          title: "Parámetros económicos: emisión y decimales",
          text: `En el contrato de un token ERC-20 se definen variables clave que controlan su comportamiento financiero:  
- **totalSupply:** cantidad total de tokens emitidos.  
- **decimals:** número de decimales que determinan la precisión del token (por ejemplo, 18 decimales como el Ether).  
Estos parámetros garantizan la consistencia en los cálculos y evitan problemas de redondeo al realizar transacciones o integraciones.`
        },
        {
          title: "Asignación y control de balances",
          text: `Cada dirección en Ethereum puede poseer tokens ERC-20, y esos saldos se almacenan en una estructura de datos interna del contrato conocida como *mapping*.  
A través de la función **balanceOf(address)**, cualquier usuario o aplicación puede consultar en tiempo real cuántos tokens posee una cuenta.  
Este principio asegura transparencia total sin necesidad de intermediarios.`
        },
        {
          title: "Transferencias y retiros parciales",
          text: `Los tokens ERC-20 permiten tanto transferencias directas como transferencias delegadas.  
Por ejemplo, si un contrato o persona autoriza a otra dirección a retirar 1,000 unidades, el sistema puede permitir retiros parciales:  
el autorizado podría extraer primero 250, y luego el resto en futuras operaciones hasta agotar el límite.  
Esto se maneja mediante las funciones **approve()** y **transferFrom()**, que otorgan gran flexibilidad en los flujos de pago automatizados.`
        }
      ]
    },
    {
      id: "funcionalidad",
      heading: "Interacción y Operaciones en Contratos ERC-20",
      text: `Los tokens ERC-20 no solo sirven como medios de pago o inversión, sino que pueden integrarse como componentes activos dentro de contratos inteligentes.  
Gracias a su diseño programable, permiten operaciones automatizadas en plataformas descentralizadas.`,
      list: [
        {
          title: "Compatibilidad con aplicaciones DeFi",
          text: `Los tokens ERC-20 se utilizan para representar activos, colaterales, préstamos o recompensas en plataformas como Aave, Compound o Uniswap.  
Su estructura estándar permite que cualquier nuevo token pueda ser incorporado sin necesidad de modificaciones en el código base.`
        },
        {
          title: "Interacción entre contratos inteligentes",
          text: `Cualquier contrato en Ethereum puede llamar a las funciones de un token ERC-20 para enviar, recibir o verificar saldos.  
Esto posibilita el desarrollo de sistemas complejos como juegos, mercados o DAOs que operan sobre múltiples activos.`
        },
        {
          title: "Gestión de permisos y seguridad",
          text: `El uso del sistema de aprobación evita que los contratos gasten fondos sin consentimiento del usuario.  
La función **allowance()** permite verificar los límites autorizados, previniendo vulnerabilidades comunes como transferencias no intencionadas.`
        }
      ]
    },
    {
      id: "beneficios",
      heading: "Ventajas Clave del Estándar ERC-20",
      text: `El éxito del ERC-20 se debe a su diseño equilibrado: simple, seguro y ampliamente compatible.  
Estas son las razones por las que se ha convertido en el estándar dominante en el ecosistema de tokens:`,
      list: [
        {
          title: "Interoperabilidad total",
          text: `Todos los tokens ERC-20 pueden moverse libremente entre carteras y aplicaciones sin conflictos de formato.  
Esto facilita la integración entre proyectos y acelera la adopción de nuevas aplicaciones.`
        },
        {
          title: "Estandarización abierta y transparente",
          text: `Cualquier desarrollador puede revisar el código del estándar, adaptarlo o mejorarlo.  
Esta apertura ha fomentado la creación de miles de tokens con distintos propósitos: utilitarios, de gobernanza, de recompensa o de pago.`
        },
        {
          title: "Simplicidad en la creación",
          text: `Con unas pocas líneas de código basadas en la interfaz ERC-20, 
se puede desplegar un token completamente funcional.  
Esto democratizó la emisión de activos digitales y permitió que startups y organizaciones experimentaran con economías propias.`
        },
        {
          title: "Auditoría y trazabilidad pública",
          text: `Todas las operaciones quedan registradas en la blockchain.  
Cualquier usuario puede verificar saldos, movimientos y direcciones, fortaleciendo la confianza en la red.`
        }
      ]
    },
    {
      id: "contexto-actual",
      heading: "Ethereum en el Contexto Global del 2025",
      text: `Ethereum continúa siendo la segunda red blockchain más importante del mundo, después de Bitcoin, 
pero es la **más utilizada** en términos de contratos inteligentes y creación de tokens.  
Su actualización a Ethereum 2.0, con la adopción del consenso *Proof of Stake*, 
ha reducido significativamente los costos de gas y el consumo energético, 
aumentando la eficiencia de las transacciones de tokens ERC-20.`,
      list: [
        {
          title: "Adopción masiva de tokens",
          text: `Más del 80% de los activos digitales creados en 2025 siguen el estándar ERC-20, 
incluyendo stablecoins, tokens de gobernanza y proyectos de identidad digital.`
        },
        {
          title: "Integración con otras cadenas",
          text: `Gracias a los puentes (*bridges*) entre blockchains, los tokens ERC-20 pueden circular también en redes como Polygon, Arbitrum, Avalanche o Binance Smart Chain, 
manteniendo su compatibilidad técnica y expandiendo su liquidez global.`
        },
        {
          title: "Ecosistema maduro y regulado",
          text: `El crecimiento del estándar ha impulsado también marcos regulatorios más claros en torno a los activos digitales, 
donde los tokens ERC-20 se reconocen como instrumentos financieros o utilitarios según su función.`
        }
      ]
    },
    {
      id: "conclusion",
      heading: "Conclusión",
      text: `Los tokens ERC-20 son mucho más que simples unidades digitales: representan el lenguaje común 
de la economía descentralizada.  
Su estructura clara, su interoperabilidad y su adopción masiva los han convertido en la piedra angular del ecosistema Ethereum.  
Gracias a ellos, hoy es posible crear universos financieros, sociales y tecnológicos completos sobre una misma red, 
sin perder compatibilidad ni seguridad.  
En el horizonte del 2025, el ERC-20 sigue siendo la base sobre la que se construyen nuevas capas de innovación en blockchain.`
    }
  ]
};

export default CAP6;
